import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../data/content";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    scrollTo(href);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 px-4 lg:px-8 pt-4"
    >
      <div
        className={`max-w-[1400px] mx-auto flex items-center justify-between rounded-2xl px-5 md:px-7 py-3 transition-[background,box-shadow,border] duration-500 ${
          scrolled
            ? "glass shadow-[0_8px_30px_rgba(11,60,145,0.12)]"
            : "bg-white/40 backdrop-blur-md border border-white/40"
        }`}
      >
        <button
          data-testid="logo-btn"
          onClick={() => handleNav("#home")}
          className="flex items-center gap-2.5 group"
        >
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-jo-primary to-jo-accent flex items-center justify-center shadow-lg shadow-jo-primary/20">
            <span className="text-white font-extrabold text-lg leading-none">J</span>
          </div>
          <div className="text-left leading-none">
            <span className="block font-bold text-jo-primary text-[17px] tracking-tight">
              Johar Softech
            </span>
            <span className="block text-[10px] tracking-[0.2em] uppercase text-jo-accent font-medium">
              IT · BPO · Healthcare
            </span>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              data-testid={`nav-${link.label.toLowerCase()}`}
              onClick={() => handleNav(link.href)}
              className="relative px-4 py-2 text-sm font-medium text-slate-700 hover:text-jo-primary transition-colors duration-300 group"
            >
              {link.label}
              <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-jo-highlight scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta"
            onClick={() => handleNav("#contact")}
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-jo-primary text-white text-sm font-semibold px-5 py-2.5 hover:bg-jo-highlight transition-colors duration-300"
          >
            Get Started <ArrowUpRight size={16} strokeWidth={2} />
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden h-10 w-10 rounded-xl bg-jo-primary/5 flex items-center justify-center text-jo-primary"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden max-w-[1400px] mx-auto mt-2 glass rounded-2xl p-4"
          >
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                onClick={() => handleNav(link.href)}
                className="block w-full text-left px-4 py-3 rounded-xl text-slate-700 font-medium hover:bg-jo-primary/5 hover:text-jo-primary transition-colors"
              >
                {link.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
